import { useState, useEffect, useMemo, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import BusinessIcon from '@mui/icons-material/Business'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import {
  useGetNetworkActConfigQuery,
  useUpdateNetworkActConfigMutation,
  useGetNetworkMembersQuery,
} from '@/core/services/network'
import { useListWaitingRoomsQuery } from '@/core/services/waiting-room'
import { useGetActsQuery } from '@/core/services/acts'
import { WaitingRoomActConfig } from '@/core/models/network.model'

import ActConfigWizard, {
  ActConfigSummaryCard,
  EstablishmentOption,
  StaffOption,
  WaitingRoomOption,
} from '../components/ActConfigWizard'

// ─────────────────────────────────────────────────────────────────────────────
// Delete confirmation dialog
// ─────────────────────────────────────────────────────────────────────────────

function DeleteConfirmDialog({
  open,
  onClose,
  onConfirm,
  actName,
}: {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  actName: string
}) {
  const { t } = useTranslation()
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs">
      <DialogTitle>
        {t('page.network.actConfig.deleteConfirm.title', { defaultValue: 'Delete act configuration?' })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          {t('page.network.actConfig.deleteConfirm.message', {
            defaultValue: 'This will permanently remove "{{name}}" from the billing configuration. This action cannot be undone.',
            name: actName,
          })}
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          {t('common:button.cancel.label', { defaultValue: 'Cancel' })}
        </Button>
        <Button onClick={onConfirm} variant="contained" color="error">
          {t('common:button.delete.label', { defaultValue: 'Delete' })}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Establishment group component
// ─────────────────────────────────────────────────────────────────────────────

function EstablishmentGroup({
  establishment,
  configs,
  establishments,
  waitingRooms,
  members,
  allActs,
  onEdit,
  onDelete,
}: {
  establishment: EstablishmentOption
  configs: WaitingRoomActConfig[]
  establishments: EstablishmentOption[]
  waitingRooms: WaitingRoomOption[]
  members: StaffOption[]
  allActs: any[]
  onEdit: (config: WaitingRoomActConfig) => void
  onDelete: (config: WaitingRoomActConfig) => void
}) {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState(true)

  const getActDetails = (actId: string) =>
    allActs.find((a: any) => a._id === actId)

  return (
    <Card variant="outlined">
      <CardContent sx={{ pb: expanded ? undefined : '16px !important' }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ cursor: 'pointer' }}
          onClick={() => setExpanded(!expanded)}
        >
          <BusinessIcon color="action" />
          <Typography variant="h6" sx={{ flex: 1 }}>
            {establishment.name}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('page.network.actConfig.actCount', {
              defaultValue: '{{count}} act(s)',
              count: configs.length,
            })}
          </Typography>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Stack>

        <Collapse in={expanded}>
          <Stack spacing={1.5} sx={{ mt: 2 }}>
            {configs.map((config, idx) => {
              const actDetails = getActDetails(config.actId)
              return (
                <ActConfigSummaryCard
                  key={`${config.actId}-${config.role}-${idx}`}
                  config={config}
                  establishments={establishments}
                  waitingRooms={waitingRooms}
                  members={members}
                  actDetails={actDetails ? {
                    _id: actDetails._id,
                    libelle: actDetails.libelle,
                    honoraire: actDetails.honoraire,
                    id_transposition: actDetails.id_transposition,
                  } : null}
                  onEdit={() => onEdit(config)}
                  onDelete={() => onDelete(config)}
                />
              )
            })}
          </Stack>
        </Collapse>
      </CardContent>
    </Card>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Main container
// ─────────────────────────────────────────────────────────────────────────────

export default function NetworkActConfig() {
  const { t } = useTranslation()
  const { networkId } = useParams<{ networkId: string }>()
  const { enqueueSnackbar } = useSnackbar()

  const { data: config, isLoading } = useGetNetworkActConfigQuery(networkId ?? '')
  const [updateConfig, { isLoading: isUpdating }] = useUpdateNetworkActConfigMutation()

  const { data: waitingRoomsData } = useListWaitingRoomsQuery(
    { query: { network: networkId }, options: { limit: 0 } },
    { skip: !networkId }
  )
  const { data: allActs = [] } = useGetActsQuery({})
  const { data: networkMembers = [] } = useGetNetworkMembersQuery(networkId ?? '', { skip: !networkId })

  const [isWizardOpen, setIsWizardOpen] = useState(false)
  const [editingConfig, setEditingConfig] = useState<WaitingRoomActConfig | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<WaitingRoomActConfig | null>(null)
  const [acts, setActs] = useState<WaitingRoomActConfig[]>([])

  useEffect(() => {
    if (config?.waitingRoomActConfigs) {
      setActs(config.waitingRoomActConfigs)
    }
  }, [config])

  const establishments = useMemo(() => {
    return networkMembers.reduce(
      (acc, member) => {
        const est = member.member?.establishment
        if (est?._id && !acc.some((e) => e._id === est._id)) {
          acc.push({ _id: est._id, name: est.name ?? est._id })
        }
        return acc
      },
      [] as EstablishmentOption[]
    )
  }, [networkMembers])

  const waitingRooms: WaitingRoomOption[] = useMemo(
    () => waitingRoomsData?.items?.map((wr) => ({ _id: wr._id, name: wr.name })) ?? [],
    [waitingRoomsData]
  )

  // Network members offered by the "only for specific practitioners" condition
  const members: StaffOption[] = useMemo(
    () =>
      networkMembers
        .filter((m) => m.member?._id)
        .map((m) => ({
          _id: m.member._id,
          name: [m.member.userInfo?.name, m.member.userInfo?.surname].filter(Boolean).join(' ') || m.member.email || m.member._id,
        }))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [networkMembers]
  )

  // Group acts by establishment
  const groupedActs = useMemo(() => {
    const groups = new Map<string, WaitingRoomActConfig[]>()
    for (const act of acts) {
      const key = act.establishmentId || '__none__'
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(act)
    }
    return groups
  }, [acts])

  // Immediate save helper — merges change into full array and PATCHes
  const saveConfig = useCallback(async (newActs: WaitingRoomActConfig[]) => {
    try {
      await updateConfig({ networkId: networkId ?? '', acts: newActs }).unwrap()
      setActs(newActs)
      enqueueSnackbar(
        t('page.network.actConfig.saveSuccess', { defaultValue: 'Configuration saved' }),
        { variant: 'success' }
      )
    } catch (err) {
      console.error('Error saving act config:', err)
      enqueueSnackbar(
        t('page.network.actConfig.saveError', { defaultValue: 'Failed to save configuration' }),
        { variant: 'error' }
      )
    }
  }, [updateConfig, networkId, enqueueSnackbar, t])

  const handleAdd = () => {
    setEditingConfig(null)
    setIsWizardOpen(true)
  }

  const handleEdit = (config: WaitingRoomActConfig) => {
    setEditingConfig(config)
    setIsWizardOpen(true)
  }

  const handleWizardSave = async (newConfig: WaitingRoomActConfig) => {
    let newActs: WaitingRoomActConfig[]

    if (editingConfig) {
      // Replace the edited config
      const idx = acts.findIndex(
        (a) =>
          a.actId === editingConfig.actId &&
          a.establishmentId === editingConfig.establishmentId &&
          a.role === editingConfig.role
      )
      newActs = [...acts]
      if (idx !== -1) {
        newActs[idx] = newConfig
      } else {
        newActs.push(newConfig)
      }
    } else {
      // Check for duplicates
      const exists = acts.find(
        (a) =>
          a.actId === newConfig.actId &&
          a.establishmentId === newConfig.establishmentId &&
          a.role === newConfig.role
      )
      if (exists) {
        enqueueSnackbar(
          t('page.network.actConfig.duplicate', {
            defaultValue: 'This act is already configured for this establishment and role.',
          }),
          { variant: 'warning' }
        )
        return
      }
      newActs = [...acts, newConfig]
    }

    await saveConfig(newActs)
    setIsWizardOpen(false)
    setEditingConfig(null)
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    const newActs = acts.filter(
      (a) =>
        !(
          a.actId === deleteTarget.actId &&
          a.establishmentId === deleteTarget.establishmentId &&
          a.role === deleteTarget.role
        )
    )
    await saveConfig(newActs)
    setDeleteTarget(null)
  }

  const deleteActName = useMemo(() => {
    if (!deleteTarget) return ''
    const act = allActs.find((a: any) => a._id === deleteTarget.actId)
    return act?.libelle ?? deleteTarget.actId
  }, [deleteTarget, allActs])

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card elevation={0} sx={{ bgcolor: 'transparent', boxShadow: 'none' }}>
        <CardHeader
          title={t('page.network.actConfig.title', { defaultValue: 'Act Configuration' })}
          subheader={t('page.network.actConfig.subtitle', {
            defaultValue: 'Configure which billing acts are automatically sent when a consultation ends. Each act is linked to an establishment and can be scoped to specific waiting rooms.',
          })}
          action={
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>
              {t('page.network.actConfig.addAct', { defaultValue: 'Add act' })}
            </Button>
          }
        />
        <CardContent>
          {acts.length === 0 ? (
            <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
              <Typography variant="body1" color="text.secondary">
                {t('page.network.actConfig.empty', {
                  defaultValue: 'No billing acts configured yet.',
                })}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('page.network.actConfig.emptyHint', {
                  defaultValue: 'Click "Add act" to set up automatic billing for consultations in this network.',
                })}
              </Typography>
              <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAdd}>
                {t('page.network.actConfig.addFirst', { defaultValue: 'Add your first act' })}
              </Button>
            </Stack>
          ) : (
            <Stack spacing={2}>
              {/* Render grouped by establishment */}
              {establishments
                .filter((est) => groupedActs.has(est._id))
                .map((est) => (
                  <EstablishmentGroup
                    key={est._id}
                    establishment={est}
                    configs={groupedActs.get(est._id) ?? []}
                    establishments={establishments}
                    waitingRooms={waitingRooms}
                    members={members}
                    allActs={allActs}
                    onEdit={handleEdit}
                    onDelete={setDeleteTarget}
                  />
                ))}

              {/* Orphan acts (establishment not in network members) */}
              {groupedActs.has('__none__') && (
                <Alert severity="warning" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    {t('page.network.actConfig.orphanWarning', {
                      defaultValue: 'Some acts are linked to establishments no longer in this network.',
                    })}
                  </Typography>
                </Alert>
              )}
              {Array.from(groupedActs.entries())
                .filter(([key]) => key !== '__none__' && !establishments.some((e) => e._id === key))
                .map(([estId, configs]) => (
                  <EstablishmentGroup
                    key={estId}
                    establishment={{ _id: estId, name: `${t('page.network.actConfig.unknownEstablishment', { defaultValue: 'Unknown establishment' })} (${estId.slice(-6)})` }}
                    configs={configs}
                    establishments={establishments}
                    waitingRooms={waitingRooms}
                    members={members}
                    allActs={allActs}
                    onEdit={handleEdit}
                    onDelete={setDeleteTarget}
                  />
                ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Wizard dialog */}
      <ActConfigWizard
        open={isWizardOpen}
        onClose={() => {
          setIsWizardOpen(false)
          setEditingConfig(null)
        }}
        onSave={handleWizardSave}
        initialConfig={editingConfig}
        establishments={establishments}
        waitingRooms={waitingRooms}
        members={members}
        isSaving={isUpdating}
      />

      {/* Delete confirmation */}
      <DeleteConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        actName={deleteActName}
      />
    </Box>
  )
}
